import { useAppSelector } from '../../../app/hooks';
import { useEffect, useState } from 'react';
import { AICA_CREATE_DEAL_DETAILS, CONFIG_STEP } from '../../ManageDealDrawer/DealDrawerConstants';
import { getDealFormConfig } from '../../../store/investor/apiHelpers';
import { PageType, StepperItem } from '../Components/Page/PageTypes';
import { useLocation, useSearchParams } from 'react-router-dom';
import { DEAL_CONFIG_SOURCES, DEAL_SOURCE_TYPES, DEAL_VIEW_TYPES } from '../../../store/investor/constants';
import { isLenderType } from '../../../utils/utils';

const useFetchConfigForDrawer = (enabled: boolean) => {
    // Redux Selectors
    const source = useAppSelector((state) => state.investor.dealApplicationDrawer.source);
    const loggedInUser = useAppSelector((state) => state.user.userData);
    const platformData = useAppSelector((state) => state.user.domainMetadata);
    const userType = useAppSelector((state) => state.user.userData.accountType);
    const location = useLocation();

    // State Variables
    const [drawerConfig, setDrawerConfig] = useState<{
        pages: PageType[] | [];
        stepper: StepperItem[] | [];
        filters: any | [];
        disabledFields: string[];
    }>({
        pages: [],
        stepper: [],
        filters: [],
        disabledFields: [],
    });

    // Extract disabled field keys from config
    const getDisabledFields = (pages: PageType[]): string[] =>
        pages?.flatMap(
            (p) =>
                p?.config?.flatMap((b) =>
                    Object.entries(b?.config || {})
                        .filter(([, f]) => f?.isDisabled)
                        .map(([k]) => k),
                ) || [],
        ) || [];
    const [loadingConfig, setLoadingConfig] = useState(false);
    const [params] = useSearchParams();
    const selectedCategory = params.get('category') || DEAL_VIEW_TYPES.DEAL_VIEW;

    const getConfigName = () =>
        DEAL_CONFIG_SOURCES.includes(source as (typeof DEAL_CONFIG_SOURCES)[number]) ? AICA_CREATE_DEAL_DETAILS : null;

    const getFilters = (configData: any) =>
        isLenderType(userType)
            ? configData?.lenderFilters
            : selectedCategory === DEAL_VIEW_TYPES.MANDATE_VIEW
            ? configData?.mandateFilters
            : configData?.dealViewFilters;

    const fetchConfig = async () => {
        const configName = getConfigName();
        if (!configName) return;

        await getDealFormConfig(
            {
                accountType: loggedInUser.accountType,
                investorOrgId: platformData?.investorOrgId,
                primaryEmail: loggedInUser?.primaryEmail,
                configName,
            },
            {
                onSuccess: (configData) => {
                    if (source === DEAL_SOURCE_TYPES.DEAL_FILTERS) {
                        setDrawerConfig({ pages: [], stepper: [], filters: getFilters(configData), disabledFields: [] });
                        return;
                    }

                    const isDealsPage = location.pathname.includes('deals') || location.pathname.includes('company-deals');
                    const pages = (configData?.pages ?? []).filter((p: any) => !isDealsPage || p.step !== CONFIG_STEP.SELECT_INVESTORS);
                    const stepper = (configData?.stepper ?? []).filter(
                        (s: any) => !isDealsPage || s.stepId !== CONFIG_STEP.SELECT_INVESTORS,
                    );

                    setDrawerConfig({ pages, stepper, filters: [], disabledFields: getDisabledFields(pages) });
                },
            },
        );
    };

    // Effects

    useEffect(() => {
        if (!enabled) return;
        setLoadingConfig(true);
        fetchConfig().then(() => {
            setLoadingConfig(false);
        });
    }, [source]);

    return {
        drawerConfig,
        loadingConfig,
    };
};

export default useFetchConfigForDrawer;
